import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Wand2, Copy, RefreshCw, Loader2, Sparkles, Check, NotebookPen, SendHorizonal } from 'lucide-react';

export const WizardPromptCard = ({ api, subAccountId, profileId, initialPrompt = '', isDarkMode, onSendToWizard }) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [copied, setCopied] = useState(false);

  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const sub = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}/wizard-prompt`);
      setPrompt(res.data.prompt);
      toast.success('AI Wizard prompt generated from this profile');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate the prompt'); }
    finally { setGenerating(false); }
  };

  const refine = async () => {
    if (!notes.trim()) { toast.error('Add instructions or paste the onboarding-call transcript first'); return; }
    setRefining(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/brand-profiles/${profileId}/wizard-prompt/refine`, { prompt, notes });
      setPrompt(res.data.prompt);
      setNotes('');
      toast.success('Prompt rewritten around your notes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Refine failed'); }
    finally { setRefining(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success('Copied — paste it into the AI Wizard build brief');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.55 }}
      className={`relative rounded-[22px] border overflow-hidden ${
        isDarkMode ? 'border-violet-400/25 bg-violet-400/[0.04]' : 'border-violet-200 bg-violet-50/40 shadow-sm'
      }`} data-testid="wizard-prompt-card">
      {isDarkMode && <div className="absolute -top-24 right-1/4 w-96 h-72 rounded-full bg-violet-500/10 blur-[100px] pointer-events-none" />}

      <div className="relative flex items-center gap-2.5 px-5 md:px-6 py-4 flex-wrap">
        <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-teal-400 flex items-center justify-center shrink-0">
          <Wand2 size={14} className="text-white" />
        </span>
        <div className="min-w-0">
          <h3 className={`font-display text-[15px] font-semibold tracking-tight ${text}`}>AI Wizard Prompt</h3>
          <p className={`text-[11px] ${sub}`}>A short build instruction for the AI Wizard — it already knows the brand profile; this only tells it what to build.</p>
        </div>
        {prompt && (
          <div className="ml-auto flex items-center gap-2 shrink-0 flex-wrap">
            <button onClick={generate} disabled={generating}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-[11px] font-semibold transition-colors disabled:opacity-50 ${border} ${sub} hover:text-teal-400`}
              data-testid="wizard-prompt-regenerate-btn">
              {generating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Regenerate
            </button>
            <button onClick={copy}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-[11px] font-semibold transition-colors ${border} ${sub} hover:text-violet-400`}
              data-testid="wizard-prompt-copy-btn">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            {onSendToWizard && (
              <button onClick={() => onSendToWizard(prompt)}
                className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-gradient-to-r from-violet-500 to-teal-400 text-white text-[11px] font-bold shadow-[0_0_22px_-8px_rgba(139,92,246,0.7)] hover:-translate-y-0.5 transition-all"
                data-testid="wizard-prompt-send-btn">
                <SendHorizonal size={12} />
                Send to AI Wizard
              </button>
            )}
          </div>
        )}
      </div>

      <div className="relative px-5 md:px-6 pb-5 md:pb-6">
        {!prompt ? (
          <div className={`rounded-2xl border border-dashed ${border} p-6 text-center`}>
            <p className={`text-xs mb-4 ${sub}`}>
              Generate a ready-to-paste AI Wizard instruction — a short brief that tells the wizard what to build
              (the wizard already knows this brand profile). Then refine it with your own instructions or the
              onboarding-call transcript.
            </p>
            <button onClick={generate} disabled={generating}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-violet-500 to-teal-400 text-white text-xs font-bold shadow-[0_0_24px_-8px_rgba(139,92,246,0.7)] hover:-translate-y-0.5 transition-all disabled:opacity-50"
              data-testid="wizard-prompt-generate-btn">
              {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Generate from this profile
            </button>
          </div>
        ) : (
          <>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
              className={`w-full px-4 py-3.5 rounded-2xl border text-[13px] leading-relaxed focus:outline-none focus:border-violet-400/60 resize-y ${
                isDarkMode ? 'bg-black/30 border-white/10 text-gray-200' : 'bg-white border-gray-200 text-gray-800'
              }`} data-testid="wizard-prompt-textarea" />

            <div className={`mt-4 rounded-2xl border p-4 ${isDarkMode ? 'border-white/[0.07] bg-white/[0.02]' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2 mb-2">
                <NotebookPen size={13} className="text-violet-400" />
                <p className={`text-xs font-semibold ${text}`}>Make it specific to this client</p>
              </div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                placeholder="Add instructions or paste the onboarding-call transcript — the brief is rewritten around their exact needs, nothing invented…"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs leading-relaxed focus:outline-none focus:border-violet-400/60 resize-y ${
                  isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/25' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                }`} data-testid="wizard-prompt-notes" />
              <div className="flex justify-end mt-2.5">
                <button onClick={refine} disabled={refining || !notes.trim()}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-violet-500/15 text-violet-300 border border-violet-400/30 text-[11px] font-bold hover:bg-violet-500/25 transition-colors disabled:opacity-40"
                  data-testid="wizard-prompt-refine-btn">
                  {refining ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {refining ? 'Refining…' : 'Refine prompt'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default WizardPromptCard;
